import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Coins, TrendingUp, Flame, Star, Target, Edit3, Check, X, Zap } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Human-readable action labels
const ACTION_LABEL = {
  post_created: "Postagens",
  content_viewed: "Visualizações",
  mission_claimed: "Missões",
  app_shared: "Compartilhamentos",
  transfer_in: "Transferências recebidas",
  ride_completed: "Corridas",
  comment_created: "Comentários",
  like_post: "Curtidas dadas",
  referral_signup: "Indicações",
  work_bonus: "Bônus de trabalho",
  investor_referral: "Comissões",
};

function labelFor(k) { return ACTION_LABEL[k] || k.replace(/_/g, " "); }

export default function WeeklyEarningsCard() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [custom, setCustom] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/coins/weekly-earnings`)
      .then((r) => setData(r.data))
      .catch(() => {});

  useEffect(() => { load(); }, []);

  const setGoal = async (goal) => {
    setSaving(true);
    try {
      await axios.put(`${API}/coins/weekly-goal`, { goal });
      toast.success(goal > 0 ? `Meta atualizada para ${goal.toLocaleString("pt-BR")} moedas` : "Meta redefinida");
      await load();
      setEditing(false);
      setCustom("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao atualizar meta");
    }
    setSaving(false);
  };

  if (!data) return null;

  const max = Math.max(1, ...data.series.map((r) => r.coins));
  const total = data.total_coins_7d || 0;

  return (
    <Card className="p-5 relative overflow-hidden" data-testid="weekly-earnings-card">
      {/* subtle glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(600px circle at 100% 0%, rgba(234,179,8,.15), transparent 40%)" }} />
      <div className="relative flex items-center justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-yellow-400/15 text-yellow-600 flex items-center justify-center shrink-0">
            <Coins className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Últimos 7 dias</div>
            <h3 className="text-lg font-bold tracking-tight">Você ganhou</h3>
            <div className="text-3xl font-black text-yellow-600 leading-none mt-1" data-testid="weekly-total">
              {total.toLocaleString("pt-BR")} <span className="text-sm font-bold text-muted-foreground">moedas</span>
            </div>
          </div>
        </div>
        <div className="text-right space-y-1">
          <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-orange-500/10 text-orange-600 text-[11px] font-bold" data-testid="weekly-streak">
            <Flame className="w-3 h-3" /> {data.streak_days} dia{data.streak_days === 1 ? "" : "s"} seguidos
          </div>
          <div className="text-[11px] text-muted-foreground flex items-center justify-end gap-1">
            <TrendingUp className="w-3 h-3" /> {data.tx_count_7d} eventos
          </div>
        </div>
      </div>

      {/* Weekly goal progress */}
      {data.weekly_goal > 0 && (
        <div className="relative mb-3" data-testid="weekly-goal">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="inline-flex items-center gap-1 text-muted-foreground font-semibold">
              <Target className="w-3 h-3" /> Meta semanal
              {data.mission_streak_days >= 2 && (
                <span className="inline-flex items-center gap-0.5 ml-1 px-1.5 py-0.5 rounded-full bg-orange-500/15 text-orange-700 text-[9px] font-black" data-testid="mission-streak-badge">
                  <Zap className="w-2.5 h-2.5" /> {data.mission_streak_days}d
                </span>
              )}
              <button
                onClick={() => { setEditing(true); setCustom(""); }}
                className="ml-1 text-muted-foreground/70 hover:text-primary"
                data-testid="weekly-goal-edit"
                aria-label="Editar meta"
              >
                <Edit3 className="w-3 h-3" />
              </button>
            </span>
            <span className="font-bold" data-testid="weekly-goal-value">
              <span className={data.goal_pct >= 100 ? "text-emerald-600" : "text-yellow-600"}>{total.toLocaleString("pt-BR")}</span>
              <span className="text-muted-foreground"> / {data.weekly_goal.toLocaleString("pt-BR")}</span>
              <span className={`ml-1 ${data.goal_pct >= 100 ? "text-emerald-600" : "text-muted-foreground"}`}>· {data.goal_pct}%</span>
            </span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${data.goal_pct >= 100 ? "bg-gradient-to-r from-emerald-400 to-emerald-600" : "bg-gradient-to-r from-yellow-400 to-amber-500"}`}
              style={{ width: `${Math.max(2, Math.min(100, data.goal_pct))}%` }}
              data-testid="weekly-goal-bar"
            />
          </div>
          {data.goal_pct >= 100 && (
            <div className="mt-1 text-[10px] text-emerald-700 font-bold">🎉 Meta batida! Continue ganhando extras.</div>
          )}
          {editing && (
            <div className="mt-3 p-3 rounded-xl border border-border bg-muted/40 space-y-2" data-testid="weekly-goal-editor">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Escolha sua meta</div>
              <div className="grid grid-cols-3 gap-1.5">
                {[500, 1000, 2000].map((g) => (
                  <button
                    key={g}
                    onClick={() => setGoal(g)}
                    disabled={saving}
                    data-testid={`weekly-goal-preset-${g}`}
                    className={`h-9 rounded-lg text-xs font-bold border transition-colors ${data.weekly_goal === g ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}
                  >
                    {g.toLocaleString("pt-BR")}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  step={50}
                  placeholder="Personalizar"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  className="h-8 text-xs flex-1"
                  data-testid="weekly-goal-custom-input"
                />
                <Button
                  size="sm"
                  onClick={() => {
                    const v = parseInt(custom, 10);
                    if (Number.isFinite(v) && v >= 0 && v <= 100000) setGoal(v);
                    else toast.error("Valor entre 0 e 100.000");
                  }}
                  disabled={saving || custom === ""}
                  className="h-8"
                  data-testid="weekly-goal-custom-save"
                >
                  <Check className="w-3 h-3" />
                </Button>
                <button
                  onClick={() => { setEditing(false); setCustom(""); }}
                  className="p-1.5 rounded-md hover:bg-muted"
                  data-testid="weekly-goal-cancel"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              {data.goal_is_custom && (
                <button
                  onClick={() => setGoal(0)}
                  disabled={saving}
                  className="text-[10px] text-muted-foreground hover:underline"
                  data-testid="weekly-goal-clear"
                >
                  Usar meta padrão da plataforma ({data.goal_platform_default.toLocaleString("pt-BR")})
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Sparkline bars */}
      <div className="relative flex items-end gap-2 h-16 mb-3" data-testid="weekly-series">        {data.series.map((r, i) => {
          const h = Math.round((r.coins / max) * 100);
          const d = new Date(r.date + "T00:00:00");
          const label = d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "");
          return (
            <div key={r.date} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${r.coins} moedas`}>
              <div
                className={`w-full rounded-t-lg transition-all ${r.coins > 0 ? "bg-gradient-to-t from-yellow-500 to-amber-300" : "bg-muted"}`}
                style={{ height: `${Math.max(6, h)}%` }}
                data-testid={`weekly-bar-${i}`}
              />
              <span className={`text-[9px] uppercase tracking-widest ${i === data.series.length - 1 ? "text-primary font-bold" : "text-muted-foreground"}`}>
                {label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Top actions */}
      {data.top_actions && data.top_actions.length > 0 && (
        <div className="relative border-t border-border pt-3 mt-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2 flex items-center gap-1">
            <Star className="w-3 h-3" /> Onde você mais ganhou
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="weekly-top-actions">
            {data.top_actions.map((a) => (
              <span
                key={a.action}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-muted/60 border border-border text-[11px]"
                data-testid={`weekly-action-${a.action}`}
              >
                <span className="font-semibold">{labelFor(a.action)}</span>
                <span className="text-yellow-600 font-bold">+{a.coins}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
